import { makeAutoObservable, runInAction } from "mobx";
import { track } from "@/lib/analytics";
import type { CapacityRebalanceService } from "@/services/capacity-rebalance.service";
import type { WorkItemService } from "@/services/work-item.service";
import type {
  TCapacityRebalanceRequest,
  TCapacityRebalanceResponse,
  TCapacityRebalanceStrategy,
  TCapacityRebalanceStrategyKey,
} from "@/types/capacity-rebalance";
import { apiFieldErrorMessage } from "@/lib/errors/api-error";

type TAsyncState = "idle" | "loading" | "success" | "error";

interface Services {
  capacityRebalanceService: CapacityRebalanceService;
  workItemService: WorkItemService;
}

function extractMessage(err: unknown): string {
  return apiFieldErrorMessage(err) ?? (err instanceof Error ? err.message : String(err));
}

/**
 * Proposals for moving one person's over-allocated hours onto their team, and
 * the apply that writes the chosen plan one assignee change at a time (not
 * atomic; each task is undoable on its own).
 */
export class CapacityRebalanceStore {
  response: TCapacityRebalanceResponse | null = null;
  activeKey: TCapacityRebalanceStrategyKey | null = null;
  state: TAsyncState = "idle";
  applyState: TAsyncState = "idle";
  errorMessage: string | null = null;
  errorDetails: string[] = [];

  private services: Services;
  private runId = 0;

  constructor(services: Services) {
    this.services = services;
    makeAutoObservable(this, { services: false, runId: false } as never, { autoBind: true });
  }

  get activeStrategy(): TCapacityRebalanceStrategy | null {
    const strategies = this.response?.strategies ?? [];
    return strategies.find((s) => s.key === this.activeKey) ?? strategies[0] ?? null;
  }

  setActiveKey(key: TCapacityRebalanceStrategyKey): void {
    this.activeKey = key;
  }

  async propose(slug: string, body: TCapacityRebalanceRequest): Promise<void> {
    const id = ++this.runId;
    this.state = "loading";
    this.applyState = "idle";
    this.errorMessage = null;
    this.errorDetails = [];
    try {
      const response = await this.services.capacityRebalanceService.propose(slug, body);
      runInAction(() => {
        if (id !== this.runId) return;
        this.response = response;
        this.activeKey = response.strategies[0]?.key ?? null;
        this.state = "success";
      });
      // Opening the dialog is what asks for proposals, so a loaded proposal set
      // is the read (NG-2060). A superseded run was never shown, so it isn't one.
      if (id === this.runId) {
        track("capacity_rebalance_viewed", {
          member_id: body.member_id,
          strategy_count: response.strategies.length,
          source: "capacity_grid",
        });
      }
    } catch (error: unknown) {
      console.error("Capacity rebalance proposal failed", { slug, body, error });
      runInAction(() => {
        if (id !== this.runId) return;
        this.state = "error";
        this.errorMessage = extractMessage(error);
      });
    }
  }

  /** Write every move of the strategy; resolves to the moves that succeeded. */
  async apply(slug: string, strategy: TCapacityRebalanceStrategy): Promise<number> {
    this.applyState = "loading";
    this.errorDetails = [];
    const results = await Promise.allSettled(
      strategy.moves.map((move) => {
        // A task with no project is written through the workspace route; the
        // project route with an empty id is a 404, not a fallback.
        const data = { assignee_ids: [move.to_member_id] };
        return move.project_id
          ? this.services.workItemService.updateWorkItem(slug, move.project_id, move.task_id, data)
          : this.services.workItemService.updateWorkspaceWorkItem(slug, move.task_id, data);
      }),
    );
    const failures = results
      .map((result, index) => ({ result, move: strategy.moves[index] }))
      .filter(({ result }) => result.status === "rejected")
      .map(({ result, move }) => {
        const reason = (result as PromiseRejectedResult).reason;
        console.error("Capacity rebalance move failed", { slug, taskId: move.task_id, reason });
        return `${move.task_name}: ${extractMessage(reason)}`;
      });
    runInAction(() => {
      this.applyState = failures.length > 0 ? "error" : "success";
      this.errorDetails = failures;
    });
    const moved = results.length - failures.length;
    // One event for the action, counting only the moves that landed (NG-2060).
    // Its own name, not task_assigned: that event is per task and per member in
    // task-analytics.ts, and a rebalance moves tasks to several people at once.
    if (moved > 0) {
      track("capacity_rebalance_applied", {
        count: moved,
        strategy: strategy.key,
        source: "capacity_grid",
      });
    }
    return moved;
  }

  reset(): void {
    this.runId++;
    this.response = null;
    this.activeKey = null;
    this.state = "idle";
    this.applyState = "idle";
    this.errorMessage = null;
    this.errorDetails = [];
  }
}
